import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import { isCreditCardExpired } from "@/Helpers/creditCardExpiry";
import { useState } from "react";
import { PaymentsMethodsDropdown } from "@/Shared/Payments/Dropdowns/PaymentsMethodsDropdown";

interface BookShipmentChangePaymentMethodDialogProps extends DialogBaseProps {
  selectedStripePaymentMethodId: string | undefined;
  cards: Array<CreditCard>;
  bankAccounts: Array<BankAccount>;
  /** Fired with the newly selected payment method. */
  onChanged: (newStripePaymentMethodId: string) => void;
}

// Select-payment-method dialog on the review screen. Replaces the legacy
// imperative modal.
export function BookShipmentChangePaymentMethodDialog(
  props: BookShipmentChangePaymentMethodDialogProps,
) {
  const [selectedStripePaymentMethodId, setSelectedStripePaymentMethodId] =
    useState(props.selectedStripePaymentMethodId);

  // The dropdown blocks selecting expired cards, but the shipment's current
  // card is preselected — and it may have expired since it was chosen (ENG-4368)
  const selectedCard = props.cards.find(
    (card) => card.stripePaymentMethodId === selectedStripePaymentMethodId,
  );
  const selectedCardIsExpired =
    selectedCard !== undefined && isCreditCardExpired(selectedCard.expiry);

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Select Payment Method"
      description="Select a payment method for this shipment"
      width="medium"
      footer={
        <Button
          primary
          disabled={
            selectedStripePaymentMethodId === undefined || selectedCardIsExpired
          }
          disabledMessage={
            selectedCardIsExpired
              ? "The selected credit card has expired. Please select a different payment method."
              : "Select a payment method"
          }
          onClick={function () {
            if (selectedStripePaymentMethodId !== undefined) {
              props.onOpenChange?.(false);
              props.onChanged(selectedStripePaymentMethodId);
            }
          }}
        >
          Done
        </Button>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        {/* The dropdown takes its width from this container, which is the
            dialog's own body width. */}
        <PaymentsMethodsDropdown
          cards={props.cards}
          bankAccounts={props.bankAccounts}
          isChequePaymentPermitted={false}
          isEFTPaymentPermitted={false}
          isETransferPaymentPermitted={false}
          isWirePaymentPermitted={false}
          selectedStripePaymentMethodId={selectedStripePaymentMethodId}
          onOptionSelected={setSelectedStripePaymentMethodId}
        />
      </ControlPresentationProvider>
    </Dialog>
  );
}
